import { cn } from "@/lib/utils";
import { 
  Sparkles, 
  Fingerprint,
  Target, 
  Layers, 
  Grid3X3, 
  Award, 
  Shield,
  Rocket, 
  CheckCircle2,
  GitBranch,
  ChevronDown,
  Menu,
  Swords
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";

interface ReportNavigationProps {
  activeSection?: string;
  onNavigate: (sectionId: string) => void;
}

const sections = [
  { id: "executive-summary", label: "Summary", icon: Sparkles },
  { id: "decision-snapshot", label: "Snapshot", icon: Fingerprint },
  { id: "success-criteria", label: "Erfolg", icon: Target },
  { id: "conflict-section", label: "Konflikt", icon: Swords },
  { id: "options", label: "Optionen", icon: Layers },
  { id: "matrix", label: "Matrix", icon: Grid3X3 },
  { id: "dominant-strategy", label: "Strategie", icon: Award },
  { id: "strategic-path", label: "Pfad", icon: GitBranch },
  { id: "commitment", label: "Commit", icon: Shield },
  { id: "action-plan", label: "Plan", icon: Rocket },
  { id: "go-decision", label: "Go/Pivot", icon: CheckCircle2 },
];

export function ReportNavigation({ activeSection, onNavigate }: ReportNavigationProps) {
  const activeItem = sections.find(s => s.id === activeSection) || sections[0];
  const ActiveIcon = activeItem.icon;

  return (
    <>
      {/* Desktop Navigation - Fixed Sidebar */}
      <nav className="fixed left-4 top-1/2 -translate-y-1/2 z-40 hidden xl:flex flex-col gap-1 bg-background/80 backdrop-blur-sm rounded-lg border p-2 shadow-lg">
        {sections.map((section) => {
          const Icon = section.icon;
          const isActive = activeSection === section.id;
          
          return (
            <button
              key={section.id}
              onClick={() => onNavigate(section.id)}
              className={cn(
                "flex items-center gap-2 px-3 py-2 rounded-md text-xs font-medium transition-all",
                "hover:bg-accent hover:text-accent-foreground",
                isActive 
                  ? "bg-primary/10 text-primary" 
                  : "text-muted-foreground"
              )}
            >
              <Icon className="h-4 w-4" />
              <span>{section.label}</span>
            </button>
          );
        })}
      </nav>

      {/* Mobile Navigation - Sticky Dropdown */}
      <div className="sticky top-[73px] z-30 xl:hidden bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b px-4 py-2">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button 
              variant="outline" 
              className="w-full justify-between gap-2 h-10"
            >
              <div className="flex items-center gap-2">
                <ActiveIcon className="h-4 w-4 text-primary" />
                <span className="font-medium">{activeItem.label}</span>
              </div>
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent 
            align="start" 
            className="w-[calc(100vw-2rem)] max-w-md bg-background border shadow-lg"
          >
            {sections.map((section) => {
              const Icon = section.icon;
              const isActive = activeSection === section.id;
              
              return (
                <DropdownMenuItem
                  key={section.id}
                  onClick={() => onNavigate(section.id)}
                  className={cn(
                    "flex items-center gap-3 py-3 cursor-pointer",
                    isActive && "bg-primary/10 text-primary"
                  )}
                >
                  <Icon className={cn("h-4 w-4", isActive ? "text-primary" : "text-muted-foreground")} />
                  <span className={cn("font-medium", isActive && "text-primary")}>{section.label}</span>
                  {isActive && (
                    <span className="ml-auto text-xs text-primary">●</span>
                  )}
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </>
  );
}
